import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { sectionsApi } from '@/services/api/endpoints/academic';
import { assignmentsApi, teacherAssignmentsApi } from '@/services/api/endpoints/teaching';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { Assignment, AssignmentKind, Submission } from '@/types/domain';
import { formatDate, humanise } from '@/utils/format';

const KINDS: AssignmentKind[] = ['HOMEWORK', 'ASSIGNMENT', 'PROJECT', 'PRACTICE', 'OTHER'];

const STATUS_TONE = {
  DRAFT: 'neutral',
  PUBLISHED: 'success',
  CLOSED: 'warning',
  ARCHIVED: 'neutral',
} as const;

const SUBMISSION_TONE = {
  NOT_SUBMITTED: 'neutral',
  SUBMITTED: 'success',
  LATE: 'warning',
  RETURNED: 'info',
} as const;

/**
 * Homework and coursework, per class and subject.
 *
 * Work is set under a teaching assignment, which already names the teacher, the
 * subject and the class — so the picker at the top of this screen is not a
 * filter but the thing that grants the right to set work at all.
 *
 * Feedback is recorded here; marks are not. Grading belongs to the Examination
 * module, and a score in both places would become two answers to one question.
 */
export function AssignmentsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [sectionId, setSectionId] = useState('');
  const [teachingAssignmentId, setTeachingAssignmentId] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [viewing, setViewing] = useState<Assignment | null>(null);
  const [feedbackFor, setFeedbackFor] = useState<Submission | null>(null);
  const [feedback, setFeedback] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    assignmentType: 'HOMEWORK' as AssignmentKind,
    assignedDate: '',
    dueDate: '',
    maxMarks: '',
    allowLateSubmission: false,
  });

  const sections = useQuery({
    queryKey: queryKeys.sections.list({ size: 200 }),
    queryFn: () => sectionsApi.list({ page: 0, size: 200 }),
  });

  const teachingAssignments = useQuery({
    queryKey: queryKeys.teacherAssignments.list({ sectionId }),
    queryFn: () => teacherAssignmentsApi.list({ sectionId }),
    enabled: sectionId !== '',
  });

  const query = useQuery({
    queryKey: queryKeys.assignments.list(teachingAssignmentId, true),
    queryFn: () => assignmentsApi.list(teachingAssignmentId, true),
    enabled: teachingAssignmentId !== '',
  });

  const submissions = useQuery({
    queryKey: queryKeys.assignments.submissions(viewing?.id ?? ''),
    queryFn: () => assignmentsApi.submissions(viewing?.id ?? ''),
    enabled: viewing !== null,
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });

  const create = useMutation({
    mutationFn: () =>
      assignmentsApi.create({
        teacherAssignmentId: teachingAssignmentId,
        title: form.title,
        description: form.description === '' ? undefined : form.description,
        assignmentType: form.assignmentType,
        assignedDate: form.assignedDate,
        dueDate: form.dueDate,
        maxMarks: form.maxMarks === '' ? undefined : Number(form.maxMarks),
        allowLateSubmission: form.allowLateSubmission,
      }),
    onSuccess: () => {
      toast.success('Assignment drafted');
      setFormOpen(false);
      setForm({ ...form, title: '', description: '', assignedDate: '', dueDate: '', maxMarks: '' });
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const publish = useMutation({
    mutationFn: (id: string) => assignmentsApi.publish(id),
    onSuccess: (assignment) => {
      toast.success(`Set for ${assignment.expectedCount} students`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const close = useMutation({
    mutationFn: (id: string) => assignmentsApi.close(id),
    onSuccess: () => {
      toast.success('Assignment closed');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const giveFeedback = useMutation({
    mutationFn: () =>
      assignmentsApi.giveFeedback(viewing?.id ?? '', feedbackFor?.id ?? '', feedback),
    onSuccess: () => {
      toast.success('Feedback recorded');
      setFeedbackFor(null);
      setFeedback('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
    },
    onError: (error) => handleApiError(error),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Assignments"
        description="Work set for a class. Comments are recorded here; marks belong to Examinations."
        action={
          can(PERMISSIONS.ASSIGNMENT_CREATE) && teachingAssignmentId !== '' ? (
            <Button onClick={() => setFormOpen(true)}>Set work</Button>
          ) : undefined
        }
      />

      <Card className="p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Class"
            value={sectionId}
            onChange={(event) => {
              setSectionId(event.target.value);
              setTeachingAssignmentId('');
            }}
          >
            <option value="">Select a class…</option>
            {sections.data?.data.map((section) => (
              <option key={section.id} value={section.id}>
                {section.gradeName !== undefined ? `${section.gradeName} · ` : ''}
                {section.name}
              </option>
            ))}
          </SelectField>

          <SelectField
            label="Subject"
            value={teachingAssignmentId}
            onChange={(event) => setTeachingAssignmentId(event.target.value)}
            disabled={sectionId === ''}
          >
            <option value="">Select a subject…</option>
            {teachingAssignments.data
              ?.filter((assignment) => assignment.status === 'ACTIVE')
              .map((assignment) => (
                <option key={assignment.id} value={assignment.id}>
                  {assignment.subjectName} · {assignment.teacherName}
                </option>
              ))}
          </SelectField>
        </div>
      </Card>

      {teachingAssignmentId === '' ? (
        <Card className="p-5">
          <p className="py-6 text-center text-sm text-subtle">
            Choose a class and subject to see the work set for it.
          </p>
        </Card>
      ) : query.isPending ? (
        <LoadingState label="Loading assignments" />
      ) : query.error !== null ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data === undefined || query.data.length === 0 ? (
        <Card className="p-5">
          <p className="py-6 text-center text-sm text-subtle">No work set for this class yet.</p>
        </Card>
      ) : (
        <Card className="p-5">
          <ul className="divide-y divide-line">
            {query.data.map((assignment) => (
              <li key={assignment.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                    {assignment.title}
                    <Badge tone={STATUS_TONE[assignment.status]}>
                      {assignment.status.toLowerCase()}
                    </Badge>
                  </p>
                  <p className="mt-0.5 text-xs text-subtle">
                    {humanise(assignment.assignmentType)} · due {formatDate(assignment.dueDate)}
                    {assignment.status !== 'DRAFT' &&
                      ` · ${assignment.submittedCount} of ${assignment.expectedCount} handed in`}
                  </p>
                </div>

                <div className="flex shrink-0 gap-1">
                  {assignment.status !== 'DRAFT' && (
                    <Button size="sm" variant="ghost" onClick={() => setViewing(assignment)}>
                      Submissions
                    </Button>
                  )}
                  {can(PERMISSIONS.ASSIGNMENT_PUBLISH) && assignment.status === 'DRAFT' && (
                    <Button size="sm" loading={publish.isPending} onClick={() => publish.mutate(assignment.id)}>
                      Set work
                    </Button>
                  )}
                  {can(PERMISSIONS.ASSIGNMENT_CLOSE) && assignment.status === 'PUBLISHED' && (
                    <Button size="sm" variant="ghost" onClick={() => close.mutate(assignment.id)}>
                      Close
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title="Set work"
        description="Saved as a draft. Publishing makes it visible and creates an entry for every child in the class."
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={create.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => create.mutate()}
              loading={create.isPending}
              disabled={form.title === '' || form.assignedDate === '' || form.dueDate === ''}
            >
              Save draft
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField
            label="Title"
            required
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Set on"
              type="date"
              required
              value={form.assignedDate}
              onChange={(event) => setForm({ ...form, assignedDate: event.target.value })}
            />
            <TextField
              label="Due"
              type="date"
              required
              value={form.dueDate}
              onChange={(event) => setForm({ ...form, dueDate: event.target.value })}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label="Type"
              required
              value={form.assignmentType}
              onChange={(event) =>
                setForm({ ...form, assignmentType: event.target.value as AssignmentKind })
              }
            >
              {KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {humanise(kind)}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Out of"
              type="number"
              min={1}
              value={form.maxMarks}
              onChange={(event) => setForm({ ...form, maxMarks: event.target.value })}
              hint="The score itself is recorded in Examinations"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 border-line text-accent focus:ring-accent"              checked={form.allowLateSubmission}
              onChange={(event) => setForm({ ...form, allowLateSubmission: event.target.checked })}
            />
            Accept work after the due date
          </label>
        </div>
      </Modal>

      <Modal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        size="lg"
        title={viewing?.title ?? 'Submissions'}
        description="Everyone who was set this work, including those who have not handed anything in."
      >
        {submissions.isPending ? (
          <LoadingState label="Loading submissions" />
        ) : submissions.data === undefined ? null : (
          <ul className="divide-y divide-line">
            {submissions.data.map((submission) => (
              <li key={submission.id} className="flex items-start justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm text-ink">{submission.studentName}</p>
                  {submission.teacherFeedback !== undefined && (
                    <p className="mt-0.5 text-xs text-muted">{submission.teacherFeedback}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge tone={SUBMISSION_TONE[submission.status]}>
                    {humanise(submission.status)}
                  </Badge>
                  {can(PERMISSIONS.ASSIGNMENT_SUBMISSION_REVIEW) &&
                    submission.status !== 'NOT_SUBMITTED' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setFeedbackFor(submission);
                          setFeedback(submission.teacherFeedback ?? '');
                        }}
                      >
                        Feedback
                      </Button>
                    )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      <Modal
        open={feedbackFor !== null}
        onClose={() => setFeedbackFor(null)}
        title={`Feedback for ${feedbackFor?.studentName ?? ''}`}
        description="Comments only — there is deliberately no mark here."
        footer={
          <>
            <Button variant="ghost" onClick={() => setFeedbackFor(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => giveFeedback.mutate()}
              loading={giveFeedback.isPending}
              disabled={feedback.trim() === ''}
            >
              Return work
            </Button>
          </>
        }
      >
        <TextField
          label="Feedback"
          required
          value={feedback}
          onChange={(event) => setFeedback(event.target.value)}
        />
      </Modal>
    </div>
  );
}
